import { useField } from '@unform/core';
import React, {
  InputHTMLAttributes,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { FiAlertCircle } from 'react-icons/fi';

import { Icon } from '../Icon';
import { colors } from '../../styles/tokens';
import {
  Container,
  ContainerError,
  ContainerInput,
  ContainerSuccess,
  Info,
  Input,
} from './styles';

interface inputProps extends InputHTMLAttributes<HTMLInputElement> {
  name: string;
  label?: string;
  labelColor?: string;
  successText?: string;
  info?: string;
}

const InputText: React.FC<inputProps> = ({
  name,
  label,
  labelColor,
  property,
  style,
  successText,
  info,
  onFocus: onFocusProp,
  onBlur: onBlurProp,
  ...rest
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [isFocused, setIsFocused] = useState(false);
  const [isFilled, setIsFilled] = useState(false);

  const { defaultValue, fieldName, error, registerField } = useField(name);

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      path: inputRef.current?.type === 'number' ? 'valueAsNumber' : 'value',
    });
  }, [fieldName, registerField]);

  // `onFocus`/`onBlur` compostos com o que o chamador passar (ex.
  // aplicação de máscara em telefone/CNPJ/CEP) em vez de deixar o
  // `{...rest}` sobrescrever silenciosamente o handler interno -- senão
  // `isFocused` nunca voltava a `false` nesses campos especificamente.
  const handleInputFocus = useCallback<NonNullable<inputProps['onFocus']>>(
    event => {
      setIsFocused(true);
      onFocusProp?.(event);
    },
    [onFocusProp],
  );

  const handleInputBlur = useCallback<NonNullable<inputProps['onBlur']>>(
    event => {
      setIsFocused(false);
      setIsFilled(!!inputRef.current?.value);
      onBlurProp?.(event);
    },
    [onBlurProp],
  );

  return (
    <Container property={property} style={style} labelColor={labelColor}>
      {label && <span>{label}</span>}
      <ContainerInput
        isErrored={!!error}
        isFocused={isFocused}
        // {...rest}
      >
        <Input
          ref={inputRef}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          defaultValue={defaultValue}
          autoCapitalize="none"
          {...rest}
        />

        {!!info && !isFilled && (
          <Info title={info}>
            <FiAlertCircle color={colors.primary} size={20} />
          </Info>
        )}
      </ContainerInput>

      {!!successText && (
        <ContainerSuccess>
          <Icon name="check_circle" size={16} color="#32a54a" />
          <span>{successText}</span>
        </ContainerSuccess>
      )}
      {error && (
        <ContainerError>
          <Icon name="error" size={16} color={colors.error} />
          <span>{error}</span>
        </ContainerError>
      )}
    </Container>
  );
};

export { InputText };
